/// <amd-module name="TFS/Admin/Groups/Pivots/Users/Redux/UsersPivotSaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { IPermissionPage, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphUser, UsersOperation } from "TFS/Admin/Groups/Common/Contracts";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getProjectName, getUrlForGraphSubject } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { UsersPivotActions, UsersPivotActionTypes } from "TFS/Admin/Groups/Pivots/Users/Redux/UsersPivotActions";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { GraphSubject, GraphSubjectQuery, PagedGraphUsers } from "VSS/Platform/Contracts/Graph";
import { IVssLocationService } from "VSS/Platform/Location";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Root work item saga.
 */
export function* usersPivotSaga(): SagaIterator {
    // Register the initialize Users handler
    yield effects.takeEvery(UsersPivotActionTypes.InitView, initializeUsersPivot);
    yield effects.takeEvery(UsersPivotActionTypes.OnRenderMissingItem, fetchMoreUsers);
    yield effects.takeLatest(UsersPivotActionTypes.SearchUsers, searchUsers);
}

export function* initializeUsersPivot(action: ActionsOfType<UsersPivotActions, UsersPivotActionTypes.InitView>): SagaIterator {
    const { scopeDescriptor, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.UsersPivot, "Initialize users pivot");
    yield effects.call(telemetryLogger.captureAttemptStart);

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    try {
        const pagedUsers: PagedGraphUsers = (yield effects.call([graphClient, graphClient.listUsers], undefined, undefined, scopeDescriptor)) as any;
        let users: CustomGraphUser[] = [];

        if (pagedUsers) {
            const projectName =
                permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

            yield effects.all(
                pagedUsers.graphUsers.map(pagedUser =>
                    effects.call(function*() {
                        const url = (yield effects.call(
                            getUrlForGraphSubject,
                            pagedUser.descriptor,
                            permissionPage,
                            locationService,
                            telemetryLogger,
                            projectName
                        )) as any;
                        users.push({ ...(pagedUser as CustomGraphUser), linkProps: { href: url } });
                    })
                )
            );

            yield effects.put(UsersPivotActions.initialUsersLoadSucceeded(users, pagedUsers.continuationToken));
        } else {
            yield effects.put(UsersPivotActions.userOperationStatus(UsersOperation.Fetch, Resources.FetchUsersFailed, MessageCardSeverity.Error));
            yield effects.put(UsersPivotActions.usersLoadFailed());
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        yield effects.put(UsersPivotActions.userOperationStatus(UsersOperation.Fetch, Resources.FetchUsersFailed, MessageCardSeverity.Error));
        yield effects.put(UsersPivotActions.usersLoadFailed());
    }
}

export function* fetchMoreUsers(action: ActionsOfType<UsersPivotActions, UsersPivotActionTypes.OnRenderMissingItem>): SagaIterator {
    const { continuationToken, scopeDescriptor, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.UsersPivot, "Fetch more users");
    yield effects.call(telemetryLogger.captureAttemptStart);
    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    if (continuationToken && continuationToken.length) {
        try {
            let pagedUsers: PagedGraphUsers = (yield effects.call(
                [graphClient, graphClient.listUsers],
                undefined,
                continuationToken.toString(),
                scopeDescriptor
            )) as any;
            let users: CustomGraphUser[] = [];

            if (pagedUsers) {
                const projectName =
                    permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

                yield effects.all(
                    pagedUsers.graphUsers.map(pagedUser =>
                        effects.call(function*() {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                pagedUser.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName
                            )) as any;
                            users.push({ ...(pagedUser as CustomGraphUser), linkProps: { href: url } });
                        })
                    )
                );

                yield effects.put(UsersPivotActions.fetchMoreUsersSucceeded(users, pagedUsers.continuationToken));
            } else {
                yield effects.put(UsersPivotActions.usersLoadFailed());
                yield effects.put(UsersPivotActions.userOperationStatus(UsersOperation.Fetch, Resources.FetchUsersFailed, MessageCardSeverity.Error));
            }
        } catch (error) {
            yield effects.call(telemetryLogger.captureError, error.message || error);
            yield effects.put(UsersPivotActions.userOperationStatus(UsersOperation.Fetch, Resources.FetchUsersFailed, MessageCardSeverity.Error));
            yield effects.put(UsersPivotActions.usersLoadFailed());
        }
    } else {
        yield effects.put(UsersPivotActions.userOperationStatus(UsersOperation.Fetch, Resources.FetchUsersFailed, MessageCardSeverity.Error));
    }
}

export function* searchUsers(action: ActionsOfType<UsersPivotActions, UsersPivotActionTypes.SearchUsers>): SagaIterator {
    const { newKeyword, oldKeyword, scopeDescriptor, isExpandSearchToOrg, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.UsersPivot, "Search Users");
    yield effects.call(telemetryLogger.captureAttemptStart);

    if (!newKeyword) {
        // Reset view
        yield effects.put(UsersPivotActions.updateSearchStatus(SearchStatus.None));
    } else if (oldKeyword !== undefined && localeIgnoreCaseComparer(newKeyword, oldKeyword) !== 0) {
        // Search

        try {
            const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
            const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

            const graphSubjectQuery: GraphSubjectQuery = { query: newKeyword, scopeDescriptor: scopeDescriptor, subjectKind: ["User"] };

            // Search
            const graphSubjects: GraphSubject[] = (yield effects.call([graphClient, graphClient.querySubjects], graphSubjectQuery)) as any;

            const resultUsers: CustomGraphUser[] = [];

            if (graphSubjects && graphSubjects.length > 0) {
                const projectName =
                    permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

                yield effects.all(
                    graphSubjects.map(graphSubject =>
                        effects.call(function*() {
                            const url = (yield effects.call(
                                getUrlForGraphSubject,
                                graphSubject.descriptor,
                                permissionPage,
                                locationService,
                                telemetryLogger,
                                projectName
                            )) as any;
                            resultUsers.push({ ...(graphSubject as CustomGraphUser), linkProps: { href: url } });
                        })
                    )
                );
            }

            yield effects.put(UsersPivotActions.updateSearchResult(newKeyword, resultUsers, false));
        } catch (error) {
            yield effects.put(UsersPivotActions.updateSearchStatus(SearchStatus.None)); // Reset view

            yield effects.call(telemetryLogger.captureError, error.message || error);
        }
    } else {
        // Search did not change
        yield effects.put(UsersPivotActions.updateSearchStatus(SearchStatus.Completed));
    }
}
